/**
 * CoursesPage — Liste des noeuds racines (themes astrologiques)
 *
 * Affiche une grille de cartes, une par noeud racine.
 * Clic sur un noeud -> page detail avec enfants et/ou contenu.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';
import type { NodeSummary } from '../types/courses.js';

/** Icones SVG simples pour chaque theme (slugs BDD) */
function ThemeIcon({ slug }: { slug: string }) {
  const s = { stroke: colors.accent, strokeWidth: '1.3', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' };

  switch (slug) {
    case 'fondamentaux':
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M3 17V5a2 2 0 012-2h10a2 2 0 012 2v12" {...s} /><path d="M3 17h14" {...s} /><path d="M7 3v14" {...s} />
      </svg>);
    case 'les-signes':
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <circle cx="11" cy="11" r="8" {...s} /><path d="M11 3v16M3 11h16" {...s} />
      </svg>);
    case 'les-4-elements':
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M11 3L4 17h14L11 3z" {...s} /><line x1="7" y1="12" x2="15" y2="12" {...s} />
      </svg>);
    case 'les-planetes':
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <circle cx="11" cy="11" r="5" {...s} /><circle cx="11" cy="11" r="1.5" fill={colors.accent} />
      </svg>);
    case 'les-aspects':
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M4 18L11 4l7 14H4z" {...s} />
      </svg>);
    case 'les-maisons':
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M3 19h16M5 19V10l6-6 6 6v9" {...s} /><path d="M9 19v-5h4v5" {...s} />
      </svg>);
    case 'techniques':
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <circle cx="11" cy="11" r="3" {...s} /><path d="M11 3v2M11 17v2M3 11h2M17 11h2M5.6 5.6l1.4 1.4M15 15l1.4 1.4M5.6 16.4l1.4-1.4M15 7l1.4-1.4" {...s} />
      </svg>);
    case 'cycles-planetaires':
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M11 3a8 8 0 110 16" {...s} /><path d="M11 19a8 8 0 110-16" {...s} strokeDasharray="3 3" /><path d="M14 6l-3-3-3 3" {...s} />
      </svg>);
    case 'les-progressions':
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M3 18L9 10l4 4 6-8" {...s} /><path d="M15 6h4v4" {...s} />
      </svg>);
    case 'planetes-retrogrades':
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M15 4l-4 4 4 4" {...s} /><path d="M11 8h6" {...s} /><circle cx="11" cy="14" r="4" {...s} />
      </svg>);
    case 'revolution-solaire':
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <circle cx="11" cy="11" r="4" {...s} /><path d="M11 3v3M11 16v3M3 11h3M16 11h3M5.6 5.6l2 2M14.4 14.4l2 2M5.6 16.4l2-2M14.4 7.6l2-2" {...s} />
      </svg>);
    default:
      return (<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <circle cx="11" cy="11" r="8" {...s} /><path d="M8 8a3 3 0 014.5 2.5c0 1.5-2.5 1.5-2.5 3" {...s} /><circle cx="11" cy="16" r=".8" fill={colors.accent} />
      </svg>);
  }
}

export function CoursesPage() {
  const [nodes, setNodes] = useState<NodeSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch<NodeSummary[]>('/nodes/roots')
      .then((res) => {
        if (res.success && res.data) {
          setNodes(res.data);
        } else {
          setError(res.error ?? 'Impossible de charger les cours');
        }
      })
      .catch(() => setError('Erreur de connexion au serveur'))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <div style={{ maxWidth: 1200, width: '100%', textAlign: 'center', padding: '48px 0', color: colors.textMuted, fontSize: 14 }}>
        Chargement des cours...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: 1200, width: '100%' }}>
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 14,
            color: '#991b1b',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <i className="bi bi-exclamation-circle" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, width: '100%' }}>
      {/* En-tete */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: colors.text, fontFamily: "'Inter', system-ui, sans-serif" }}>
          Mes cours
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: colors.textMuted }}>
          {nodes.length} theme{nodes.length > 1 ? 's' : ''} astrologique{nodes.length > 1 ? 's' : ''}
        </p>
      </div>

      {/* Grille des noeuds racines */}
      {nodes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted, fontSize: 14 }}>
          Aucun cours disponible pour le moment.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
          {nodes.map((node) => (
            <Link
              key={node.id}
              to={`/cours/${node.id}`}
              style={{
                display: 'block',
                background: '#fff',
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                padding: '18px 16px',
                textDecoration: 'none',
                transition: 'border-color .15s, box-shadow .15s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = colors.accentLine;
                (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = colors.border;
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: colors.accentLight,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ThemeIcon slug={node.slug} />
                </div>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: colors.textLight, marginTop: 2 }}>
                  <path d="M5 3l5 4-5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.text }}>
                {node.nom}
              </h3>
              {node.description && (
                <p style={{
                  margin: '4px 0 0', fontSize: 13, color: colors.textMuted,
                  lineHeight: 1.4, overflow: 'hidden',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  {node.description}
                </p>
              )}
              {node.childrenCount !== undefined && node.childrenCount > 0 && (
                <p style={{ margin: '10px 0 0', fontSize: 12, color: colors.textLight }}>
                  {node.childrenCount} sous-rubrique{node.childrenCount > 1 ? 's' : ''}
                </p>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
